import React, { useCallback, useState } from 'react';
import { Upload, FileText, Image, X, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface UploadedFile {
  file: File;
  url: string;
  type: 'pdf' | 'png';
}

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  selectedFile: UploadedFile | null;
  isSignedIn: boolean;
  onAuthRequired: () => void;
  isBlocked?: boolean;
  onBlockedAttempt?: () => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ onFileSelect, selectedFile, isSignedIn, onAuthRequired, isBlocked, onBlockedAttempt }) => {
  const { t } = useTranslation('inspire');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    // Check file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
    if (!allowedTypes.includes(file.type)) {
      return t('errors.invalidFileType');
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return t('errors.fileTooLarge');
    }

    return null;
  };

  const handleFileChange = useCallback((file: File) => {
    // Check if recruiter trying to select file
    if (isBlocked && onBlockedAttempt) {
      onBlockedAttempt();
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    onFileSelect(file);
  }, [onFileSelect, isBlocked, onBlockedAttempt]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileChange(files[0]);
    }
  }, [handleFileChange]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileChange(files[0]);
    }
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLLabelElement>) => {
    if (!isSignedIn) {
      e.preventDefault(); // Block file picker
      onAuthRequired(); // Show auth modal
    }
  };

  const clearFile = () => {
    setError(null);
    // Note: We don't have a clear function from parent, so we just clear the error
  };

  return (
    <div className="space-y-3">
      {selectedFile ? (
        <div className="flex items-center justify-between p-2 sm:p-3 bg-green-50 border border-green-200 rounded-lg gap-2">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            {selectedFile.type === 'pdf' ? (
              <FileText className="text-green-600 flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
            ) : (
              <Image className="text-green-600 flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5" />
            )}
            <div className="min-w-0">
              <p className="text-[10px] sm:text-sm font-medium text-green-800 truncate">{selectedFile.file.name}</p>
              <p className="text-[8px] sm:text-xs text-green-600">
                {(selectedFile.file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
          <label 
            onClick={handleButtonClick}
            className="inline-flex items-center gap-1 sm:gap-1.5 px-1.5 py-0.5 sm:px-3 sm:py-1.5 bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 text-white font-medium rounded-lg hover:from-pink-600 hover:via-orange-500 hover:to-yellow-500 transition-all shadow-sm hover:shadow-md cursor-pointer text-[10px] sm:text-sm flex-shrink-0"
          >
            <Upload size={12} className="sm:w-[14px] sm:h-[14px]" />
            {t('upload.changeFile')}
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleInputChange}
              className="hidden"
            />
          </label>
        </div>
      ) : (
        <label 
          onClick={handleButtonClick}
          className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 text-white font-medium rounded-lg hover:from-pink-600 hover:via-orange-500 hover:to-yellow-500 transition-all shadow-md hover:shadow-lg cursor-pointer text-xs sm:text-sm"
        >
          <Upload size={12} className="sm:w-[14px] sm:h-[14px]" />
          {t('upload.chooseCvFile')}
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={handleInputChange}
            className="hidden"
          />
        </label>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <AlertCircle className="text-red-600 mr-2 flex-shrink-0" size={16} />
          <span className="text-sm text-red-800">{error}</span>
          <button
            onClick={clearFile}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
